import {
  OpensteerProtocolError,
  isOpensteerProtocolError,
  type CookieRecord,
  type OpensteerCookieQueryOutput,
  type OpensteerActionResult,
  type OpensteerAddInitScriptInput,
  type OpensteerAddInitScriptOutput,
  type OpensteerComputerExecuteInput,
  type OpensteerComputerExecuteOutput,
  type OpensteerComputerKeyModifier,
  type OpensteerComputerMouseButton,
  type OpensteerNetworkDetailOutput,
  type OpensteerNetworkQueryInput,
  type OpensteerNetworkQueryOutput,
  type OpensteerOpenInput,
  type OpensteerOpenOutput,
  type OpensteerPageActivateInput,
  type OpensteerPageActivateOutput,
  type OpensteerPageCloseInput,
  type OpensteerPageCloseOutput,
  type OpensteerPageEvaluateInput,
  type OpensteerPageEvaluateOutput,
  type OpensteerPageGotoInput,
  type OpensteerPageGotoOutput,
  type OpensteerPageListInput,
  type OpensteerPageListOutput,
  type OpensteerPageNewInput,
  type OpensteerPageNewOutput,
  type OpensteerRequestBodyInput,
  type OpensteerRequestResponseResult,
  type OpensteerSessionCloseOutput,
  type OpensteerSessionFetchInput,
  type OpensteerSessionInfo,
  type OpensteerStateQueryOutput,
  type OpensteerStorageArea,
  type OpensteerStorageDomainSnapshot,
  type OpensteerTargetInput,
} from "@opensteer/protocol";

import {
  OpensteerBrowserManager,
  type OpensteerBrowserStatus,
  type WorkspaceBrowserManifest,
} from "../browser-manager.js";
import { resolveOpensteerEnvironment } from "../env.js";
import { normalizeThrownOpensteerError } from "../internal/errors.js";
import type { OpensteerProviderOptions } from "../provider/config.js";
import type {
  OpensteerInterceptScriptOptions,
  OpensteerInstrumentableRuntime,
  OpensteerRouteOptions,
  OpensteerRouteRegistration,
} from "./instrumentation.js";
import type { OpensteerRuntimeOptions } from "./runtime.js";
import {
  createOpensteerSemanticRuntime,
  resolveOpensteerRuntimeConfig,
} from "./runtime-resolution.js";
import type { OpensteerDisconnectableRuntime } from "./semantic-runtime.js";

export interface OpensteerTargetOptions {
  readonly element?: number;
  readonly selector?: string;
  readonly persist?: string;
  readonly captureNetwork?: string;
}

export interface OpensteerClickOptions extends OpensteerTargetOptions {
  readonly button?: OpensteerComputerMouseButton;
  readonly clickCount?: number;
  readonly modifiers?: readonly OpensteerComputerKeyModifier[];
}

export interface OpensteerInputOptions extends OpensteerTargetOptions {
  readonly text: string;
  readonly pressEnter?: boolean;
}

export interface OpensteerScrollOptions extends OpensteerTargetOptions {
  readonly direction: "up" | "down" | "left" | "right";
  readonly amount: number;
}

export interface OpensteerExtractOptions {
  readonly persist: string;
}

export interface OpensteerWaitForPageOptions {
  readonly openerPageRef?: string;
  readonly urlIncludes?: string;
  readonly timeoutMs?: number;
  readonly pollIntervalMs?: number;
}

export type OpensteerAddInitScriptOptions = OpensteerAddInitScriptInput;
export type OpensteerGotoOptions = Omit<OpensteerPageGotoInput, "url">;
export type OpensteerNetworkQueryOptions = OpensteerNetworkQueryInput;
export type OpensteerNetworkQueryResult = OpensteerNetworkQueryOutput;
export type OpensteerNetworkDetailResult = OpensteerNetworkDetailOutput;
export type OpensteerFetchOptions = Omit<OpensteerSessionFetchInput, "url" | "body"> & {
  readonly body?: string | OpensteerRequestBodyInput;
};
export type OpensteerComputerExecuteOptions = OpensteerComputerExecuteInput;
export type OpensteerStorageMap = Readonly<Record<string, string>>;
export type OpensteerBrowserState = OpensteerStateQueryOutput;

export interface OpensteerCookieJar {
  readonly domain?: string;
  has(name: string): boolean;
  get(name: string): string | undefined;
  getAll(): readonly CookieRecord[];
  serialize(): string;
}

export interface OpensteerDomController {
  click(input: OpensteerClickOptions): Promise<OpensteerActionResult>;
  hover(input: OpensteerTargetOptions): Promise<OpensteerActionResult>;
  input(input: OpensteerInputOptions): Promise<OpensteerActionResult>;
  scroll(input: OpensteerScrollOptions): Promise<OpensteerActionResult>;
}

export interface OpensteerNetworkController {
  query(input?: OpensteerNetworkQueryOptions): Promise<OpensteerNetworkQueryResult>;
  detail(
    recordId: string,
    options?: { readonly probe?: boolean },
  ): Promise<OpensteerNetworkDetailResult>;
}

export interface OpensteerOptions extends OpensteerRuntimeOptions {
  readonly provider?: OpensteerProviderOptions;
}

export interface OpensteerBrowserCloneOptions {
  readonly sourceUserDataDir: string;
  readonly sourceProfileDirectory?: string;
}

export interface OpensteerBrowserController {
  status(): Promise<OpensteerBrowserStatus>;
  clone(input: OpensteerBrowserCloneOptions): Promise<WorkspaceBrowserManifest>;
  reset(): Promise<void>;
  delete(): Promise<void>;
}

class SessionCookieJar implements OpensteerCookieJar {
  readonly domain?: string;
  private readonly cookies: readonly CookieRecord[];

  constructor(output: OpensteerCookieQueryOutput) {
    if (output.domain !== undefined) {
      this.domain = output.domain;
    }
    this.cookies = output.cookies;
  }

  has(name: string): boolean {
    return this.cookies.some((cookie) => cookie.name === name);
  }

  get(name: string): string | undefined {
    return this.cookies.find((cookie) => cookie.name === name)?.value;
  }

  getAll(): readonly CookieRecord[] {
    return this.cookies;
  }

  serialize(): string {
    return this.cookies.map((cookie) => `${cookie.name}=${cookie.value}`).join("; ");
  }
}

function createSdkProtocolError(error: unknown, fallbackMessage: string): OpensteerProtocolError {
  const normalized = normalizeThrownOpensteerError(error, fallbackMessage);
  return new OpensteerProtocolError(normalized.code, normalized.message, {
    cause: error,
    retriable: normalized.retriable,
    ...(normalized.capability === undefined ? {} : { capability: normalized.capability }),
    ...(normalized.details === undefined ? {} : { details: normalized.details }),
  });
}

async function wrapSdkError<T>(operation: string, fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (isOpensteerProtocolError(error)) {
      throw error;
    }
    throw createSdkProtocolError(error, `${operation} failed`);
  }
}

export class Opensteer {
  private readonly runtime: OpensteerDisconnectableRuntime;
  private readonly browserManager: OpensteerBrowserManager | undefined;
  readonly browser: OpensteerBrowserController;
  readonly dom: OpensteerDomController;
  readonly network: OpensteerNetworkController;

  constructor(options: OpensteerOptions = {}) {
    try {
      const environment = resolveOpensteerEnvironment(options.rootDir);
      const { provider, engineName, ...runtimeOptions } = options;
      const runtimeConfig = resolveOpensteerRuntimeConfig({
        ...(provider === undefined ? {} : { provider }),
        environment,
      });

      if (runtimeConfig.provider.mode === "cloud") {
        this.browserManager = undefined;
        this.runtime = createOpensteerSemanticRuntime({
          ...(provider === undefined ? {} : { provider }),
          ...(engineName === undefined ? {} : { engine: engineName }),
          environment,
          runtimeOptions,
        });
        this.browser = createUnsupportedBrowserController();
      } else {
        this.browserManager = new OpensteerBrowserManager({
          ...(runtimeOptions.rootDir === undefined ? {} : { rootDir: runtimeOptions.rootDir }),
          ...(runtimeOptions.rootPath === undefined ? {} : { rootPath: runtimeOptions.rootPath }),
          ...(runtimeOptions.workspace === undefined
            ? {}
            : { workspace: runtimeOptions.workspace }),
          ...(engineName === undefined ? {} : { engineName }),
          environment,
          ...(runtimeOptions.browser === undefined ? {} : { browser: runtimeOptions.browser }),
          ...(runtimeOptions.launch === undefined ? {} : { launch: runtimeOptions.launch }),
          ...(runtimeOptions.context === undefined ? {} : { context: runtimeOptions.context }),
        });
        this.runtime = createOpensteerSemanticRuntime({
          ...(provider === undefined ? {} : { provider }),
          ...(engineName === undefined ? {} : { engine: engineName }),
          environment,
          runtimeOptions: {
            ...runtimeOptions,
            rootPath: this.browserManager.rootPath,
            cleanupRootOnClose: this.browserManager.cleanupRootOnDisconnect,
          },
        });
        this.browser = {
          status: () => wrapSdkError("browser.status", () => this.browserManager!.status()),
          clone: (input) =>
            wrapSdkError("browser.clone", () => this.browserManager!.clonePersistentBrowser(input)),
          reset: () => wrapSdkError("browser.reset", () => this.browserManager!.reset()),
          delete: () => wrapSdkError("browser.delete", () => this.browserManager!.delete()),
        };
      }

      this.dom = {
        click: (input) => this.click(input),
        hover: (input) => this.hover(input),
        input: (input) => this.input(input),
        scroll: (input) => this.scroll(input),
      };

      this.network = {
        query: (input = {}) =>
          wrapSdkError("network.query", () => this.runtime.queryNetwork(input)),
        detail: (recordId, options) =>
          wrapSdkError("network.detail", () =>
            this.runtime.getNetworkDetail({ recordId, ...options }),
          ),
      };
    } catch (error) {
      if (isOpensteerProtocolError(error)) {
        throw error;
      }
      throw createSdkProtocolError(error, "Failed to initialize Opensteer");
    }
  }

  async open(input: string | OpensteerOpenInput = {}): Promise<OpensteerOpenOutput> {
    return wrapSdkError("session.open", () =>
      this.runtime.open(typeof input === "string" ? { url: input } : input),
    );
  }

  async info(): Promise<OpensteerSessionInfo> {
    return wrapSdkError("session.info", () => this.runtime.info());
  }

  async listPages(input: OpensteerPageListInput = {}): Promise<OpensteerPageListOutput> {
    return wrapSdkError("page.list", () => this.runtime.listPages(input));
  }

  async newPage(input: OpensteerPageNewInput = {}): Promise<OpensteerPageNewOutput> {
    return wrapSdkError("page.new", () => this.runtime.newPage(input));
  }

  async activatePage(input: OpensteerPageActivateInput): Promise<OpensteerPageActivateOutput> {
    return wrapSdkError("page.activate", () => this.runtime.activatePage(input));
  }

  async closePage(input: OpensteerPageCloseInput = {}): Promise<OpensteerPageCloseOutput> {
    return wrapSdkError("page.close", () => this.runtime.closePage(input));
  }

  async goto(url: string, options: OpensteerGotoOptions = {}): Promise<OpensteerPageGotoOutput> {
    return wrapSdkError("page.goto", () =>
      this.runtime.goto({
        url,
        ...options,
      }),
    );
  }

  async evaluate(
    input: string | OpensteerPageEvaluateInput,
  ): Promise<OpensteerPageEvaluateOutput["value"]> {
    return wrapSdkError("page.evaluate", async () => {
      const normalized =
        typeof input === "string"
          ? {
              script: input,
            }
          : input;
      return (await this.runtime.evaluate(normalized)).value;
    });
  }

  async addInitScript(
    input: string | OpensteerAddInitScriptInput,
  ): Promise<OpensteerAddInitScriptOutput> {
    return wrapSdkError("page.addInitScript", () =>
      this.runtime.addInitScript(
        typeof input === "string"
          ? {
              script: input,
            }
          : input,
      ),
    );
  }

  async click(input: OpensteerClickOptions): Promise<OpensteerActionResult> {
    return wrapSdkError("dom.click", () => {
      const { button, clickCount, modifiers, ...target } = input;
      return this.runtime.click({
        ...normalizeTargetOptions(target),
        ...(button === undefined ? {} : { button }),
        ...(clickCount === undefined ? {} : { clickCount }),
        ...(modifiers === undefined ? {} : { modifiers }),
      });
    });
  }

  async hover(input: OpensteerTargetOptions): Promise<OpensteerActionResult> {
    return wrapSdkError("dom.hover", () => this.runtime.hover(normalizeTargetOptions(input)));
  }

  async input(input: OpensteerInputOptions): Promise<OpensteerActionResult> {
    return wrapSdkError("dom.input", () =>
      this.runtime.input({
        ...normalizeTargetOptions(input),
        text: input.text,
        ...(input.pressEnter === undefined ? {} : { pressEnter: input.pressEnter }),
      }),
    );
  }

  async scroll(input: OpensteerScrollOptions): Promise<OpensteerActionResult> {
    return wrapSdkError("dom.scroll", () =>
      this.runtime.scroll({
        ...normalizeTargetOptions(input),
        direction: input.direction,
        amount: input.amount,
      }),
    );
  }

  async extract(input: OpensteerExtractOptions): Promise<unknown> {
    return wrapSdkError("extract", async () => (await this.runtime.extract(input)).data);
  }

  async waitForPage(
    input: OpensteerWaitForPageOptions = {},
  ): Promise<OpensteerPageListOutput["pages"][number]> {
    return wrapSdkError("page.waitForPage", async () => {
      const baseline = new Set((await this.runtime.listPages()).pages.map((page) => page.pageRef));
      const timeoutAt = Date.now() + (input.timeoutMs ?? 30_000);
      const pollIntervalMs = input.pollIntervalMs ?? 100;

      while (true) {
        const match = (await this.runtime.listPages()).pages.find((page) => {
          if (baseline.has(page.pageRef)) {
            return false;
          }
          if (input.openerPageRef !== undefined && page.openerPageRef !== input.openerPageRef) {
            return false;
          }
          if (input.urlIncludes !== undefined && !page.url.includes(input.urlIncludes)) {
            return false;
          }
          return true;
        });
        if (match !== undefined) {
          return match;
        }
        if (Date.now() >= timeoutAt) {
          throw new OpensteerProtocolError("timeout", "waitForPage timed out");
        }
        await delay(pollIntervalMs);
      }
    });
  }

  async cookies(domain?: string): Promise<OpensteerCookieJar> {
    return wrapSdkError(
      "session.cookies",
      async () =>
        new SessionCookieJar(await this.runtime.getCookies(domain === undefined ? {} : { domain })),
    );
  }

  async storage(
    domain?: string,
    type: OpensteerStorageArea = "local",
  ): Promise<OpensteerStorageMap> {
    return wrapSdkError("session.storage", async () => {
      const snapshot = await this.runtime.getStorageSnapshot(
        domain === undefined ? {} : { domain },
      );
      const domainSnapshot = pickStorageDomainSnapshot(snapshot, domain);
      if (domainSnapshot === undefined) {
        return {};
      }
      const entries =
        type === "local" ? domainSnapshot.localStorage : domainSnapshot.sessionStorage;
      return Object.fromEntries(entries.map((entry) => [entry.key, entry.value]));
    });
  }

  async state(domain?: string): Promise<OpensteerBrowserState> {
    return wrapSdkError("session.state", () =>
      this.runtime.getBrowserState(domain === undefined ? {} : { domain }),
    );
  }

  async fetch(url: string, options: OpensteerFetchOptions = {}): Promise<Response> {
    return wrapSdkError("session.fetch", async () => {
      const input = buildFetchInput(url, options);
      const result = await this.runtime.fetch(input);
      if (result.response === undefined) {
        throw new OpensteerProtocolError(
          "operation-failed",
          result.note ?? `session.fetch did not produce a response for ${url}`,
        );
      }
      return toResponse(result.response);
    });
  }

  async computerExecute(
    input: OpensteerComputerExecuteOptions,
  ): Promise<OpensteerComputerExecuteOutput> {
    return wrapSdkError("session.computerExecute", () => this.runtime.computerExecute(input));
  }

  async route(input: OpensteerRouteOptions): Promise<OpensteerRouteRegistration> {
    return wrapSdkError("session.route", () =>
      this.requireOwnedInstrumentationRuntime("route").route(input),
    );
  }

  async interceptScript(
    input: OpensteerInterceptScriptOptions,
  ): Promise<OpensteerRouteRegistration> {
    return wrapSdkError("session.interceptScript", () =>
      this.requireOwnedInstrumentationRuntime("interceptScript").interceptScript(input),
    );
  }

  async close(): Promise<OpensteerSessionCloseOutput> {
    return wrapSdkError("session.close", async () => {
      if (this.browserManager === undefined || this.browserManager.mode === "temporary") {
        return this.runtime.close();
      }

      const output = await this.runtime.close();
      await this.browserManager.close();
      return output;
    });
  }

  async disconnect(): Promise<void> {
    return wrapSdkError("session.disconnect", () => this.runtime.disconnect());
  }

  private requireOwnedInstrumentationRuntime(
    method: "route" | "interceptScript",
  ): OpensteerInstrumentableRuntime {
    if (
      typeof (this.runtime as Partial<OpensteerInstrumentableRuntime>).route === "function" &&
      typeof (this.runtime as Partial<OpensteerInstrumentableRuntime>).interceptScript ===
        "function"
    ) {
      return this.runtime as OpensteerDisconnectableRuntime & OpensteerInstrumentableRuntime;
    }
    throw new OpensteerProtocolError(
      "unsupported-operation",
      `${method}() is not available for this session runtime.`,
    );
  }
}

function createUnsupportedBrowserController(): OpensteerBrowserController {
  const fail = async (): Promise<never> => {
    throw new OpensteerProtocolError(
      "unsupported-operation",
      "browser.* helpers are only available in local mode.",
    );
  };

  return {
    status: fail,
    clone: fail,
    reset: fail,
    delete: fail,
  };
}

function normalizeTargetOptions(input: OpensteerTargetOptions): {
  readonly target: OpensteerTargetInput;
  readonly persist?: string;
  readonly captureNetwork?: string;
} {
  const hasElement = input.element !== undefined;
  const hasSelector = input.selector !== undefined;
  if (hasElement && hasSelector) {
    throw new OpensteerProtocolError(
      "invalid-argument",
      "Specify exactly one of element, selector, or persist.",
    );
  }

  if (hasElement) {
    return {
      target: {
        kind: "element",
        element: input.element,
      },
      ...(input.persist === undefined ? {} : { persist: input.persist }),
      ...(input.captureNetwork === undefined ? {} : { captureNetwork: input.captureNetwork }),
    };
  }

  if (hasSelector) {
    return {
      target: {
        kind: "selector",
        selector: input.selector,
      },
      ...(input.persist === undefined ? {} : { persist: input.persist }),
      ...(input.captureNetwork === undefined ? {} : { captureNetwork: input.captureNetwork }),
    };
  }

  if (input.persist === undefined) {
    throw new OpensteerProtocolError(
      "invalid-argument",
      "Specify exactly one of element, selector, or persist.",
    );
  }

  return {
    target: {
      kind: "persist",
      persist: input.persist,
    },
    ...(input.captureNetwork === undefined ? {} : { captureNetwork: input.captureNetwork }),
  };
}

function pickStorageDomainSnapshot(
  snapshot: {
    readonly domains: readonly OpensteerStorageDomainSnapshot[];
  },
  domain: string | undefined,
): OpensteerStorageDomainSnapshot | undefined {
  if (snapshot.domains.length === 0) {
    return undefined;
  }
  if (domain === undefined) {
    return snapshot.domains[0];
  }
  return snapshot.domains.find((entry) => entry.domain === domain);
}

function buildFetchInput(url: string, options: OpensteerFetchOptions): OpensteerSessionFetchInput {
  const { body, ...rest } = options;
  return {
    url,
    ...rest,
    ...(body === undefined ? {} : { body: normalizeFetchBody(body, rest.headers) }),
  };
}

function normalizeFetchBody(
  body: string | OpensteerRequestBodyInput,
  headers: OpensteerSessionFetchInput["headers"],
): OpensteerRequestBodyInput {
  if (typeof body !== "string") {
    return body;
  }

  const contentType = findHeaderValue(headers, "content-type");
  return contentType === undefined ? { text: body } : { text: body, contentType };
}

function findHeaderValue(
  headers: OpensteerSessionFetchInput["headers"],
  headerName: string,
): string | undefined {
  if (headers === undefined) {
    return undefined;
  }

  const match = Object.entries(headers).find(
    ([name]) => name.toLowerCase() === headerName.toLowerCase(),
  );
  return match === undefined ? undefined : String(match[1]);
}

function toResponse(response: OpensteerRequestResponseResult): Response {
  return new Response(decodeBody(response), {
    status: response.status,
    statusText: response.statusText,
    headers: Object.fromEntries(response.headers.map((header) => [header.name, header.value])),
  });
}

function decodeBody(response: OpensteerRequestResponseResult): Uint8Array | undefined {
  if (response.body === undefined) {
    return undefined;
  }
  return Uint8Array.from(Buffer.from(response.body.data, "base64"));
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
